import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';

import { RequireAuth, RequirePermission, RequirePlatformAdmin } from '@/components/common/access';
import { LoadingState } from '@/components/common/states';
import { ForbiddenPage, NotFoundPage } from '@/app/pages/error-pages';
import { LoginPage } from '@/features/auth/pages/login-page';

/*
 * Route-level code splitting: the sign-in page loads first, and everything behind
 * authentication arrives only when someone reaches it.
 *
 * The shell is split too, not just the feature areas. It carries the navigation, the
 * command palette and the animation runtime, and an anonymous visitor at the sign-in
 * screen has no use for any of it — eagerly importing it put roughly 60 kB gzipped of
 * authenticated-only code on the critical path of the one page every user sees first.
 */
const DashboardLayout = lazy(() =>
  import('@/app/layouts/dashboard-layout').then((m) => ({ default: m.DashboardLayout })),
);
const DashboardPage = lazy(() =>
  import('@/features/dashboard/pages/dashboard-page').then((m) => ({ default: m.DashboardPage })),
);
const RolesPage = lazy(() =>
  import('@/features/rbac/pages/roles-page').then((m) => ({ default: m.RolesPage })),
);
const OrganisationsPage = lazy(() =>
  import('@/features/organisations/pages/organisations-page').then((m) => ({
    default: m.OrganisationsPage,
  })),
);
const UsersPage = lazy(() =>
  import('@/features/users/pages/users-page').then((m) => ({ default: m.UsersPage })),
);
const FeatureFlagsPage = lazy(() =>
  import('@/features/feature-flags/pages/feature-flags-page').then((m) => ({
    default: m.FeatureFlagsPage,
  })),
);
const BrandingPage = lazy(() =>
  import('@/features/branding/pages/branding-page').then((m) => ({ default: m.BrandingPage })),
);
const OrganisationPage = lazy(() =>
  import('@/features/org/pages/organisation-page').then((m) => ({ default: m.OrganisationPage })),
);
const CoursesPage = lazy(() =>
  import('@/features/courses/pages/courses-page').then((m) => ({ default: m.CoursesPage })),
);
const CourseDetailPage = lazy(() =>
  import('@/features/courses/pages/course-detail-page').then((m) => ({
    default: m.CourseDetailPage,
  })),
);
const ContentPage = lazy(() =>
  import('@/features/content/pages/content-page').then((m) => ({ default: m.ContentPage })),
);
const EnrolmentsPage = lazy(() =>
  import('@/features/enrolment/pages/enrolments-page').then((m) => ({ default: m.EnrolmentsPage })),
);
const AssessmentsPage = lazy(() =>
  import('@/features/assessment/pages/assessments-page').then((m) => ({ default: m.AssessmentsPage })),
);
const AssessmentDetailPage = lazy(() =>
  import('@/features/assessment/pages/assessment-detail-page').then((m) => ({
    default: m.AssessmentDetailPage,
  })),
);
const GradingPage = lazy(() =>
  import('@/features/grading/pages/grading-page').then((m) => ({ default: m.GradingPage })),
);
const AttendancePage = lazy(() =>
  import('@/features/attendance/pages/attendance-page').then((m) => ({ default: m.AttendancePage })),
);
const SessionDetailPage = lazy(() =>
  import('@/features/attendance/pages/session-detail-page').then((m) => ({
    default: m.SessionDetailPage,
  })),
);
const AuditPage = lazy(() =>
  import('@/features/audit/pages/audit-page').then((m) => ({ default: m.AuditPage })),
);
const CertificatesPage = lazy(() =>
  import('@/features/certificates/pages/certificates-page').then((m) => ({
    default: m.CertificatesPage,
  })),
);
const CertificateDetailPage = lazy(() =>
  import('@/features/certificates/pages/certificate-detail-page').then((m) => ({
    default: m.CertificateDetailPage,
  })),
);
const VerifyPage = lazy(() =>
  import('@/features/certificates/pages/verify-page').then((m) => ({ default: m.VerifyPage })),
);
const FormsPage = lazy(() =>
  import('@/features/forms/pages/forms-page').then((m) => ({ default: m.FormsPage })),
);
const FormBuilderPage = lazy(() =>
  import('@/features/forms/pages/form-builder-page').then((m) => ({ default: m.FormBuilderPage })),
);
const WorkflowsPage = lazy(() =>
  import('@/features/workflows/pages/workflows-page').then((m) => ({ default: m.WorkflowsPage })),
);
const WorkflowBuilderPage = lazy(() =>
  import('@/features/workflows/pages/workflow-builder-page').then((m) => ({
    default: m.WorkflowBuilderPage,
  })),
);
const ApprovalsPage = lazy(() =>
  import('@/features/workflows/pages/approvals-page').then((m) => ({ default: m.ApprovalsPage })),
);
const WhitelistingPage = lazy(() =>
  import('@/features/whitelisting/pages/whitelisting-page').then((m) => ({
    default: m.WhitelistingPage,
  })),
);
const NotificationsPage = lazy(() =>
  import('@/features/notifications/pages/notifications-page').then((m) => ({
    default: m.NotificationsPage,
  })),
);
const ReportingPage = lazy(() =>
  import('@/features/reporting/pages/reporting-page').then((m) => ({ default: m.ReportingPage })),
);
const IntegrationsPage = lazy(() =>
  import('@/features/integrations/pages/integrations-page').then((m) => ({
    default: m.IntegrationsPage,
  })),
);

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingState />}>{children}</Suspense>;
}

/** Opt in to the React Router v7 behaviours now, so the upgrade is not a migration. */
export const routerFutureFlags = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
} as const;

export const router = createBrowserRouter(
  [
    { path: '/login', element: <LoginPage /> },
    { path: '/verify/:code', element: <Lazy><VerifyPage /></Lazy> },
    {
      element: <RequireAuth />,
      children: [
        {
          element: (
            <Lazy>
              <DashboardLayout />
            </Lazy>
          ),
          children: [
            { index: true, element: <Lazy><DashboardPage /></Lazy> },
            { path: 'forbidden', element: <ForbiddenPage /> },

            {
              element: <RequirePlatformAdmin />,
              children: [
                { path: 'organisations', element: <Lazy><OrganisationsPage /></Lazy> },
              ],
            },
            {
              element: <RequirePermission permission="user.view" flag="user_management" />,
              children: [{ path: 'users', element: <Lazy><UsersPage /></Lazy> }],
            },
            {
              element: <RequirePermission permission="role.view" flag="rbac" />,
              children: [{ path: 'roles', element: <Lazy><RolesPage /></Lazy> }],
            },
            {
              element: <RequirePermission permission="feature_flag.view" />,
              children: [{ path: 'modules', element: <Lazy><FeatureFlagsPage /></Lazy> }],
            },
            {
              element: <RequirePermission permission="form.view" flag="forms" />,
              children: [
                { path: 'forms', element: <Lazy><FormsPage /></Lazy> },
                { path: 'forms/:id', element: <Lazy><FormBuilderPage /></Lazy> },
              ],
            },
            {
              element: <RequirePermission permission="workflow.view" flag="workflows" />,
              children: [
                { path: 'workflows', element: <Lazy><WorkflowsPage /></Lazy> },
                { path: 'workflows/:id', element: <Lazy><WorkflowBuilderPage /></Lazy> },
                { path: 'approvals', element: <Lazy><ApprovalsPage /></Lazy> },
              ],
            },
            {
              element: <RequirePermission permission="whitelist.view" flag="whitelisting" />,
              children: [{ path: 'whitelisting', element: <Lazy><WhitelistingPage /></Lazy> }],
            },
            {
              element: <RequirePermission permission="notification.view" flag="notifications" />,
              children: [{ path: 'notifications', element: <Lazy><NotificationsPage /></Lazy> }],
            },
            {
              element: <RequirePermission permission="report.view" flag="reporting" />,
              children: [{ path: 'reporting', element: <Lazy><ReportingPage /></Lazy> }],
            },
            {
              element: <RequirePermission permission="integration.view" flag="integrations" />,
              children: [{ path: 'integrations', element: <Lazy><IntegrationsPage /></Lazy> }],
            },
            {
              element: <RequirePermission permission="course.view" flag="courses" />,
              children: [
                { path: 'courses', element: <Lazy><CoursesPage /></Lazy> },
                { path: 'courses/:id', element: <Lazy><CourseDetailPage /></Lazy> },
              ],
            },
            {
              element: <RequirePermission permission="content.view" flag="content" />,
              children: [
                {
                  path: 'courses/:courseId/content/:versionId',
                  element: <Lazy><ContentPage /></Lazy>,
                },
              ],
            },
            {
              element: <RequirePermission permission="enrolment.view" flag="enrolment" />,
              children: [{ path: 'enrolments', element: <Lazy><EnrolmentsPage /></Lazy> }],
            },
            {
              element: <RequirePermission permission="assessment.view" flag="assessment" />,
              children: [
                { path: 'assessments', element: <Lazy><AssessmentsPage /></Lazy> },
                { path: 'assessments/:id', element: <Lazy><AssessmentDetailPage /></Lazy> },
              ],
            },
            {
              element: <RequirePermission permission="grade.view" flag="grading" />,
              children: [{ path: 'grading', element: <Lazy><GradingPage /></Lazy> }],
            },
            {
              element: <RequirePermission permission="attendance_session.view" flag="attendance" />,
              children: [
                { path: 'attendance', element: <Lazy><AttendancePage /></Lazy> },
                { path: 'attendance/:id', element: <Lazy><SessionDetailPage /></Lazy> },
              ],
            },
            {
              element: <RequirePermission permission="org_unit.view" flag="org" />,
              children: [{ path: 'organisation', element: <Lazy><OrganisationPage /></Lazy> }],
            },
            {
              element: <RequirePermission permission="brand.view" flag="branding" />,
              children: [{ path: 'branding', element: <Lazy><BrandingPage /></Lazy> }],
            },
            {
              element: <RequirePermission permission="certificate.view" flag="certificates" />,
              children: [
                { path: 'certificates', element: <Lazy><CertificatesPage /></Lazy> },
                { path: 'certificates/:id', element: <Lazy><CertificateDetailPage /></Lazy> },
              ],
            },
            {
              element: <RequirePermission permission="audit.view" flag="audit_trail" />,
              children: [{ path: 'audit', element: <Lazy><AuditPage /></Lazy> }],
            },

            { path: '*', element: <NotFoundPage /> },
          ],
        },
      ],
    },
  ],
  { future: routerFutureFlags },
);
